import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cx from 'classnames';
import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useMemo, useState } from 'react';

import { fetchCurrencies } from '../../../services/currency/list';
import { setCurrentCurrency } from '../../../services/currency/set';
import Button, { getVariant } from '../../ui/button';
import Select from '../../ui/inputs/selectv2';

export const loadCurrencies = async ({ queryKey: [, , locale] }) => {
  const data = await fetchCurrencies({ locale });

  const dataFinal = data.map((e) => ({
    value: e.code,
    symbol: e.symbol,
    label: `(${e.symbol}) ${e.code}`,
    selected: e.selected,
  }));

  return dataFinal;
};

const CurrencySelector = () => {
  const { locale = 'en' } = useRouter();
  const { t } = useTranslation();
  const client = useQueryClient();
  const [error, setError] = useState<Array<string>>([]);

  const { data: currencies, isLoading: isLoadingCurrencies } = useQuery(
    ['user-dependent', 'currencies-list', locale],
    loadCurrencies
  );

  const { mutate, isLoading: isMutating } = useMutation(
    ({ locale, currency }: { locale: string; currency: string }) =>
      setCurrentCurrency({
        locale,
        currency,
        csrftoken: Cookies.get('csrftoken') || '',
      }),
    {
      onSuccess: (_, variables) => {
        window.location.reload();

        client.setQueryData(
          ['user-dependent', 'currencies-list', locale],
          currencies?.map((element) => {
            const isNewSelected = element.value === variables.value;
            if (element.selected && !isNewSelected) {
              return { ...element, selected: false };
            }
            if (isNewSelected) {
              return { ...element, selected: true };
            }

            return element;
          })
        );
      },
      onError: (error) => {
        const errorMessages: Array<string> = [];

        Object.keys(error).map((key) =>
          errorMessages.push(`${key}: ${error[key]}`)
        );

        setError(errorMessages);
      },
    }
  );

  const current = useMemo(() => {
    if (currencies) {
      const currentCurrency = currencies.find((c) => c.selected);
      return currentCurrency;
    }

    return undefined;
  }, [currencies]);

  return (
    <div className="flex">
      {currencies?.map((currency) =>
        currency.value === current?.value ? (
          <div
            key={`lang-${currency.value}`}
            className={cx(
              'h-12 px-4 py-3 w-full text-center',
              'bg-neutral-80 text-white'
            )}
          >
            {currency.label}
          </div>
        ) : (
          <Button
            key={`lang-${currency.value}`}
            variant={currency.value === current?.value ? 'DARK' : 'DEFAULT'}
            onClick={() => mutate({ locale, currency: currency.value })}
          >
            {currency.label}
          </Button>
        )
      )}
    </div>
  );
};

export default CurrencySelector;
